package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.CompanyResponsiblePersonsListViewModel
import com.example.mystybag.appCore.model.pages.CompanyResponsiblePerson
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.compose.painterResource

@Composable
fun CompanyResponsiblePersonsListView(viewModel: CompanyResponsiblePersonsListViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()

    if (rememberIsWebDesktopLayout()) {
        CompanyResponsiblePersonsListWebView(viewModel = viewModel, uiState = uiState)
        return
    }

    UIView(viewModel = viewModel) {
        CompanyResponsiblePersonsListContent(uiState = uiState, viewModel = viewModel, modifier = Modifier.fillMaxSize())
    }
}

@Composable
private fun CompanyResponsiblePersonsListWebView(
    viewModel: CompanyResponsiblePersonsListViewModel,
    uiState: com.example.mystybag.appCore.model.pages.CompanyResponsiblePersonsListUIState
) {
    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = false,
        removeVerticalPadding = true
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            ClientWebHeaderBar(
                selectedTab = 3,
                homeTabIndex = 0,
                deliveryTabIndex = 1,
                favoritesTabIndex = 2,
                menuTabIndex = 3,
                searchCallbacks = null,
                onFilterClick = {},
                showSearchAndFilter = false,
                showBackButton = true,
                onBackClick = { viewModel.navCallbacks.backButtonCallback() },
                centerTitle = sharedString(SR.strings.company_responsible_persons)
            )
            WebHeaderBottomDivider()
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
                contentAlignment = Alignment.TopCenter
            ) {
                CompanyResponsiblePersonsListContent(
                    uiState = uiState,
                    viewModel = viewModel,
                    modifier = Modifier
                        .width(1080.dp)
                        .fillMaxHeight()
                        .padding(horizontal = p16, vertical = p8)
                )
            }
        }
    }
}

@Composable
private fun CompanyResponsiblePersonsListContent(
    uiState: com.example.mystybag.appCore.model.pages.CompanyResponsiblePersonsListUIState,
    viewModel: CompanyResponsiblePersonsListViewModel,
    modifier: Modifier
) {
    val colors = AppTheme.current.colors
    Box(modifier = modifier) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = p16)
                    .widthIn(max = 412.dp)
                    .align(Alignment.TopCenter)
            ) {
                when {
                    uiState.isLoading -> {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(p16),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(color = colors.primary)
                        }
                    }
                    uiState.persons.isEmpty() -> {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(p16),
                            contentAlignment = Alignment.Center
                        ) {
                            Texts.BodyMedium(
                                text = sharedString(SR.strings.no_responsible_persons),
                                color = colors.onSurfaceVariant
                            )
                        }
                    }
                    else -> {
                        LazyColumn(
                            modifier = Modifier
                                .fillMaxSize()
                                .scrollEdgeFade(showTop = false),
                            verticalArrangement = Arrangement.spacedBy(p16)
                        ) {
                            items(uiState.persons, key = { it.id }) { person ->
                                CompanyResponsiblePersonCard(
                                    person = person,
                                    onClick = { viewModel.navigateToEditPerson(person.id) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CompanyResponsiblePersonCard(
    person: CompanyResponsiblePerson,
    onClick: () -> Unit = {}
) {
    val colors = AppTheme.current.colors

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .border(width = p1, color = colors.outline, shape = RoundedCornerShape(p20)),
        shape = RoundedCornerShape(p20),
        colors = CardDefaults.cardColors(containerColor = colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = p0)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p20),
            horizontalArrangement = Arrangement.spacedBy(p16),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(p4)
            ) {
                val name = listOfNotNull(person.firstName, person.lastName).filter { it.isNotEmpty() }.joinToString(" ")
                if (name.isNotEmpty()) {
                    Texts.TitleMedium(
                        text = name,
                        color = colors.onSurface
                    )
                }
                val phone = listOfNotNull(person.dialCode, person.phoneNumber).filter { it.isNotEmpty() }.joinToString(" ")
                if (phone.isNotEmpty()) {
                    Texts.BodyMedium(text = phone, color = colors.onSurfaceVariant)
                }
            }

            Box(
                modifier = Modifier
                    .size(p40)
                    .background(color = colors.surfaceContainerLowest, shape = CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    painter = painterResource(SRC.images.staff_edit_icon),
                    contentDescription = sharedString(SR.strings.edit_short),
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.size(p20)
                )
            }
        }
    }
}
